import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Edit, Sparkles, Loader2, Trophy, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import InstagramStoryExport from "../components/InstagramStoryExport";

export default function WeekDetail() {
  const { leagueId, weekId } = useParams();
  const [week, setWeek] = useState(null);
  const [league, setLeague] = useState(null);
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [storyExportOpen, setStoryExportOpen] = useState(false);
  const [storyData, setStoryData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [weekRes, leagueRes] = await Promise.all([
          api.getWeek(weekId),
          api.getLeague(leagueId),
        ]);
        setWeek(weekRes.data);
        setLeague(leagueRes.data);
        
        const gameRes = await api.getGame(leagueRes.data.game_id);
        setGame(gameRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [weekId, leagueId]);

  const generateAICommentary = async () => {
    setGeneratingAI(true);
    try {
      const response = await api.generateCommentary(weekId);
      setWeek((prev) => ({
        ...prev,
        ai_headline: response.data.headline,
        ai_commentators: response.data.commentators,
      }));
      toast.success("AI yorumları oluşturuldu!");
    } catch (error) {
      toast.error("AI yorumları oluşturulamadı");
    } finally {
      setGeneratingAI(false);
    }
  };

  const openStoryExport = async () => {
    try {
      const response = await api.exportWeekStory(weekId);
      setStoryData(response.data);
      setStoryExportOpen(true);
    } catch (error) {
      toast.error("Story verisi yüklenemedi");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  if (!week) return null;

  const sortedPlayers = [...week.player_stats].sort(
    (a, b) => (week.rankings?.[a.user_id] || 4) - (week.rankings?.[b.user_id] || 4)
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="week-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to={`/league/${leagueId}`}>
            <Button variant="ghost" size="icon" className="text-[#B1C1C1] hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Hafta {week.week_number}
            </h1>
            <div className="flex items-center gap-2 text-[#B1C1C1] text-sm mt-1">
              <Calendar className="w-4 h-4" />
              <span>{week.date}</span>
              <span className="text-[#64748B]">•</span>
              <span>{league?.name}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={openStoryExport}
            variant="outline"
            className="border-pink-500 text-pink-400 hover:bg-pink-500/10"
            data-testid="instagram-story-btn"
          >
            <Instagram className="w-4 h-4 mr-2" />
            Story
          </Button>
          <Button
            onClick={generateAICommentary}
            disabled={generatingAI}
            variant="outline"
            className="border-[#C6A87C] text-[#C6A87C] hover:bg-[#C6A87C]/10"
            data-testid="generate-ai-btn"
          >
            {generatingAI ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Üretiliyor...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                AI Yorum
              </>
            )}
          </Button>
          <Link to={`/league/${leagueId}/week/${weekId}/edit`}>
            <Button className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90">
              <Edit className="w-4 h-4 mr-2" />
              Düzenle
            </Button>
          </Link>
        </div>
      </div>

      {/* AI Headline */}
      {week.ai_headline && (
        <div className="glass-card p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C6A87C] to-[#8C7350] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-[#050E2E]" />
            </div>
            <div>
              <p className="text-xs text-[#C6A87C] uppercase tracking-wider mb-1">🗞 Haftanın Manşeti</p>
              <h2 className="text-2xl font-bold font-['Barlow_Condensed'] text-white">
                "{week.ai_headline}"
              </h2>
            </div>
          </div>
        </div>
      )}

      {/* 3 Commentators Section */}
      {week.ai_commentators && (
        <div className="glass-card p-6 mb-8" data-testid="commentators-section">
          <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-6">
            🗞 Haftanın Yorumu
          </h3>
          
          <div className="space-y-6">
            {/* Ahmet Çakar */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-[#1E3A5F]/50 to-transparent border-l-4 border-[#C6A87C]">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">🎤</span>
                <span className="font-bold text-[#C6A87C] font-['Barlow_Condensed'] uppercase tracking-wider">
                  Ahmet Çakar
                </span>
              </div>
              <p className="text-[#B1C1C1] italic leading-relaxed">
                {week.ai_commentators.ahmet_cakar}
              </p>
            </div>

            {/* Erman Toroğlu */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-[#1E3A5F]/50 to-transparent border-l-4 border-[#4DFFFF]">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">🎤</span>
                <span className="font-bold text-[#4DFFFF] font-['Barlow_Condensed'] uppercase tracking-wider">
                  Erman Toroğlu
                </span>
              </div>
              <p className="text-[#B1C1C1] italic leading-relaxed">
                {week.ai_commentators.erman_toroglu}
              </p>
            </div>

            {/* Rasim Ozan Kütahyalı */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-[#1E3A5F]/50 to-transparent border-l-4 border-[#FF6B6B]">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">🎤</span>
                <span className="font-bold text-[#FF6B6B] font-['Barlow_Condensed'] uppercase tracking-wider">
                  Rasim Ozan Kütahyalı
                </span>
              </div>
              <p className="text-[#B1C1C1] italic leading-relaxed">
                {week.ai_commentators.rasim_ozan}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sortedPlayers.map((player, index) => {
          const rank = week.rankings?.[player.user_id] || index + 1;
          const points = week.points?.[player.user_id] || 0;
          const playerInfo = league?.players?.find((p) => p.user_id === player.user_id);

          return (
            <div
              key={player.user_id}
              className="glass-card overflow-hidden"
              data-testid={`player-result-${player.username}`}
            >
              <div
                className={`px-6 py-4 border-b border-white/10 ${
                  rank === 1
                    ? "bg-gradient-to-r from-[#C6A87C]/20 to-transparent"
                    : "bg-gradient-to-r from-[#0A1635] to-[#0F1F45]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className={`rank-badge rank-${rank}`}>{rank}</span>
                    <div>
                      <h3 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                        {playerInfo?.name || player.username}
                      </h3>
                      <p className="text-sm text-[#64748B]">@{player.username}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-[#4DFFFF]">+{points}</span>
                    <p className="text-xs text-[#64748B]">puan</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  {game?.fields?.map((field) => (
                    <div key={field.name} className="flex items-center justify-between">
                      <span className="text-sm text-[#64748B]">{field.label}</span>
                      <span className="text-sm font-semibold text-white">
                        {field.field_type === "boolean"
                          ? player.stats[field.name]
                            ? "Evet"
                            : "Hayır"
                          : player.stats[field.name] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Instagram Story Export */}
      <InstagramStoryExport
        type="week"
        data={storyData}
        isOpen={storyExportOpen}
        onClose={() => setStoryExportOpen(false)}
      />
    </div>
  );
}
